/**
 * QuickFinding — rapid entry: tap area → snap photo → voice note → auto-classify.
 *
 * Designed for fast walkthrough capture where the evaluator doesn't want to
 * fill out a full finding form. Creates a draft finding in the offline store,
 * queues it for sync, and lets the evaluator refine later.
 */

import { useState, useCallback } from 'react'
import { Zap } from 'lucide-react'
import { AreaSelector } from './AreaSelector'
import { PhotoCapture } from './PhotoCapture'
import { VoiceRecorder } from './VoiceRecorder'
import {
  SURVEY_DOMAINS,
  type FacilityArea,
  type SurveyDomain,
  type SeverityLevel,
} from '../../lib/services/survey'
import { surveyStore, type PendingFinding } from '../../lib/offline/surveyStore'
import { enqueueFinding } from '../../lib/services/surveySync'

interface QuickFindingProps {
  visitId: string
  surveyId: string
  defaultArea?: FacilityArea | null
  onCreated?: (finding: PendingFinding) => void
}

export function QuickFinding({ visitId, surveyId, defaultArea = null, onCreated }: QuickFindingProps) {
  const [area, setArea] = useState<FacilityArea | null>(defaultArea)
  const [domain, setDomain] = useState<SurveyDomain>('ENV')
  const [severity, setSeverity] = useState<SeverityLevel>(2)
  const [observation, setObservation] = useState('')
  const [photoIds, setPhotoIds] = useState<string[]>([])
  const [isSaving, setIsSaving] = useState(false)

  const handleSave = useCallback(async () => {
    if (!area || !observation.trim()) return
    setIsSaving(true)

    const finding: PendingFinding = {
      finding_id: crypto.randomUUID(),
      survey_id: surveyId,
      visit_id: visitId,
      domain,
      standard_ref: '', // to be refined later
      severity,
      observation: observation.trim(),
      area,
      photo_ids: photoIds,
      audio_transcript: null,
      created_at: new Date().toISOString(),
    }

    await surveyStore.saveFinding(finding)
    await enqueueFinding(visitId, surveyId, finding.finding_id)
    onCreated?.(finding)

    // Reset for next capture
    setObservation('')
    setPhotoIds([])
    setIsSaving(false)
  }, [area, domain, severity, observation, photoIds, visitId, surveyId, onCreated])

  return (
    <div className="app-surface p-4 space-y-3">
      <div className="flex items-center gap-2">
        <Zap size={16} className="text-[var(--brand-accent)]" />
        <span className="section-title text-base">Quick Finding</span>
      </div>

      <AreaSelector value={area} onChange={setArea} variant="chips" />

      <div className="flex items-center gap-2">
        <select
          value={domain}
          onChange={(e) => setDomain(e.target.value as SurveyDomain)}
          className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:border-[var(--brand-primary)] focus:outline-none focus:ring-2 focus:ring-blue-100"
        >
          {SURVEY_DOMAINS.map((d) => (
            <option key={d} value={d}>{d}</option>
          ))}
        </select>

        <select
          value={severity}
          onChange={(e) => setSeverity(Number(e.target.value) as SeverityLevel)}
          className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:border-[var(--brand-primary)] focus:outline-none focus:ring-2 focus:ring-blue-100"
        >
          {([1, 2, 3] as const).map((s) => (
            <option key={s} value={s}>Sev {s}</option>
          ))}
        </select>
      </div>

      <textarea
        placeholder="Observation — type or use voice…"
        value={observation}
        onChange={(e) => setObservation(e.target.value)}
        rows={3}
        className="block w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:border-[var(--brand-primary)] focus:outline-none focus:ring-2 focus:ring-blue-100 resize-none"
      />

      <div className="flex items-center gap-2">
        {area && (
          <PhotoCapture
            visitId={visitId}
            surveyId={surveyId}
            area={area}
            onCapture={(photo) => setPhotoIds((prev) => [...prev, photo.photo_id])}
          />
        )}
        <VoiceRecorder
          onTranscript={(r) => setObservation((prev) => prev ? `${prev}\n${r.text}` : r.text)}
        />
      </div>

      {photoIds.length > 0 && (
        <p className="text-xs text-slate-500">{photoIds.length} photo(s) attached</p>
      )}

      <button
        type="button"
        disabled={!area || !observation.trim() || isSaving}
        onClick={handleSave}
        className="btn-primary w-full py-2.5 text-sm disabled:opacity-60"
      >
        {isSaving ? 'Saving…' : 'Save Finding'}
      </button>
    </div>
  )
}
